import React from 'react';
import { ordersApi } from '@/services/api';
import { formatDate, formatNumber, formatSummaryAmount } from '@/utils/formatters';
import BaseCompletedOrders, { CompletedSummary } from '@/components/orders/BaseCompletedOrders';
import TableWrapper from '@/components/orders/TableWrapper';
import styles from '@/components/orders/OrderManagement.module.css';
import shared from '@/components/utils/shared.module.css';
import OrderStatusPill from '@/components/orders/orderStatus';

interface EtfCompletedOrder {
  id: number;
  order_date: string;
  scheme: { id: number; code: string; currency?: string };
  company: { id: number; symbol: string };
  client: { id: number; code: string; name: string };
  quantity: number;
  amount: string;
  broker: { id: number; code: string } | null;
  order_status_display: string;
}

interface SchemeRow {
  key: string;
  scheme: string;
  symbol: string;
  orderCount: number;
  totalQuantity: number;
  totalAmount: number;
  // One row is one scheme, so one currency — an ETF amount is not always rupees.
  currency: string | null;
  broker: { id: number; code: string } | null;
}

interface EtfCompletedData {
  orders: EtfCompletedOrder[];
  aggregated: SchemeRow[];
}

const aggregateByScheme = (orders: EtfCompletedOrder[]): SchemeRow[] => {
  const map = new Map<string, SchemeRow>();
  for (const order of orders) {
    const key = `${order.scheme.code}__${order.company.symbol}`;
    const amount = parseFloat(order.amount) || 0;
    const existing = map.get(key);
    if (existing) {
      existing.orderCount += 1;
      existing.totalQuantity += order.quantity;
      existing.totalAmount += amount;
      if (!existing.broker && order.broker) existing.broker = order.broker;
    } else {
      map.set(key, {
        key,
        scheme: order.scheme.code,
        symbol: order.company.symbol,
        orderCount: 1,
        totalQuantity: order.quantity,
        totalAmount: amount,
        currency: order.scheme.currency ?? null,
        broker: order.broker,
      });
    }
  }
  return Array.from(map.values());
};

const fetchSide = async (
  fetcher: (p: object) => Promise<{
    calculated_orders?: EtfCompletedOrder[];
    count: number;
    total_quantity: number;
    total_amount: string;
  }>,
): Promise<{ summary: CompletedSummary; data: EtfCompletedData }> => {
  const res = await fetcher({ page_size: 500 });
  const orders = res.calculated_orders ?? [];
  return {
    summary: {
      totalCount: res.count,
      totalQty: res.total_quantity,
      totalAmt: res.total_amount,
    },
    data: {
      orders,
      aggregated: aggregateByScheme(orders),
    },
  };
};

const renderSubSections = (data: EtfCompletedData) => (
  <>
    {/* Scheme-wise summary */}
    <div className={styles['sub-section']}>
      <div className={styles['sub-section-header']}>
        <div className={styles['sub-section-line']} />
        <span className={styles['badge-non-nri']}>Scheme-wise Summary</span>
        <div className={styles['sub-section-line']} />
      </div>
      <TableWrapper>
        <table className={`${shared.table} ${styles.table}`}>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Scheme</th>
              <th className={styles.num}>Orders</th>
              <th className={styles.num}>Total Qty</th>
              <th className={styles.num}>Total Amount</th>
              <th>Broker</th>
            </tr>
          </thead>
          <tbody>
            {data.aggregated.map(row => (
              <tr key={row.key}>
                <td>{row.symbol}</td>
                <td>{row.scheme}</td>
                <td className={styles.num}>{row.orderCount}</td>
                <td className={`${styles['text-mono']} ${styles.num}`}>{formatNumber(row.totalQuantity, 0)}</td>
                <td className={`${styles['text-mono']} ${styles.num}`}>{formatSummaryAmount(row.totalAmount, row.currency)}</td>
                <td>{row.broker ? row.broker.code : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrapper>
    </div>

    {/* All client orders */}
    <div className={styles['sub-section']}>
      <div className={styles['sub-section-header']}>
        <div className={styles['sub-section-line']} />
        <span className={styles['badge-client-wise']}>All Client Orders</span>
        <div className={styles['sub-section-line']} />
      </div>
      <TableWrapper>
        <table className={`${shared.table} ${styles.table}`}>
          <thead>
            <tr>
              <th>Order Date</th>
              <th>Scheme</th>
              <th>Symbol</th>
              <th>Client Code</th>
              <th>Client Name</th>
              <th className={styles.num}>Quantity</th>
              <th className={styles.num}>Amount</th>
              <th>Broker</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map(order => (
              <tr key={order.id}>
                <td>{formatDate(order.order_date)}</td>
                <td>{order.scheme.code}</td>
                <td>{order.company.symbol}</td>
                <td>{order.client.code}</td>
                <td>{order.client.name}</td>
                <td className={`${styles['text-mono']} ${styles.num}`}>{formatNumber(order.quantity, 0)}</td>
                <td className={`${styles['text-mono']} ${styles.num}`}>{formatSummaryAmount(order.amount, order.scheme.currency ?? null)}</td>
                <td>{order.broker ? order.broker.code : '—'}</td>
                <td><OrderStatusPill stage="completed" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrapper>
    </div>
  </>
);

const EtfCompletedOrders: React.FC = () => (
  <BaseCompletedOrders<EtfCompletedData, EtfCompletedData>
    titlePrefix="ETF"
    fetchBuyOrders={() => fetchSide(ordersApi.getCompletedEtfBuyOrders)}
    fetchSellOrders={() => fetchSide(ordersApi.getCompletedEtfSellOrders)}
    renderBuySubSections={renderSubSections}
    renderSellSubSections={renderSubSections}
    isBuyEmpty={(d) => d.aggregated.length === 0}
    isSellEmpty={(d) => d.aggregated.length === 0}
  />
);

export default EtfCompletedOrders;